import React, { useState, useEffect, useRef } from "react";
import {
  FaInstagram,
  FaPaperPlane,
  FaCheckDouble,
  FaCheck,
} from "react-icons/fa";
import { MdCancel, MdRefresh } from "react-icons/md";
import { BsEmojiSmile, BsImage } from "react-icons/bs";

/* ── Static demo data ─────────────────────────────────────────────────────
   The first inbound message simulates the Meta webhook that the dashboard
   received. Everything here is hard-coded for the demo.
   ───────────────────────────────────────────────────────────────────────── */
const DEMO_INBOUND = [
  {
    id: "m1",
    direction: "in",
    text: "Hi! I saw your reel about the House of basho villa in Goa  Is it available for the 20th–23rd of next month?",
    time: "05:15 PM",
    status: "seen",
  },
];

//From ZS
//Hi Dhruvi! Thanks for reaching out to Zorba Stays. Yes, Villa Coco is available for those dates. Would you like me to share the brochure and pricing?
const DEMO_OUTBOUND = [
  // {
  //   id: "m2",
  //   direction: "out",
  //   text: "Hi Dhruvi! Thanks for reaching out to Zorba Stays. Yes, Villa Coco is available for those dates. Would you like me to share the brochure and pricing?",
  //   time: "10:44 AM",
  //   status: "seen",
  // },
  // {
  //   id: "m3",
  //   direction: "in",
  //   text: "Yes please! And do you have anything closer to the beach?",
  //   time: "10:45 AM",
  //   status: "seen",
  // },
  // {
  //   id: "m4",
  //   direction: "out",
  //   text: "Absolutely 🌊 We have two options — Villa Coco (5 min walk) and Casa Azul (beachfront). Sending both brochures now.",
  //   time: "10:46 AM",
  //   status: "delivered",
  // },
];

/* ────────────────────────────────────────────── Bubble ───────────────────────────────────────── */
function Bubble({ msg }) {
  const isIn = msg.direction === "in";
  return (
    <div className={`flex ${isIn ? "justify-start" : "justify-end"} mb-2.5`}>
      {isIn && (
        <div className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 text-white">
          <FaInstagram size={13} />
        </div>
      )}
      <div
        className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-snug shadow-sm ${
          isIn
            ? "rounded-bl-sm bg-gray-100 text-gray-800"
            : "rounded-br-sm bg-gradient-to-br from-[#3897f0] to-[#833ab4] text-white"
        }`}
      >
        <p className="whitespace-pre-wrap break-words">{msg.text}</p>
        <div
          className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${
            isIn ? "text-gray-400" : "text-white/70"
          }`}
        >
          {msg.time}
          {!isIn &&
            (msg.status === "seen" ? (
              <FaCheckDouble size={9} className="text-white" />
            ) : (
              <FaCheck size={9} />
            ))}
        </div>
      </div>
    </div>
  );
}

/* ── Main demo chat panel ───────────────────────────────────────────────── */
export default function InstagramDemoChat({ lead, onClose }) {
  const [messages, setMessages] = useState([...DEMO_INBOUND, ...DEMO_OUTBOUND]);
  const [draft, setDraft] = useState("");
  const [simulating, setSimulating] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom whenever messages change
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  const nowTime = () =>
    new Date().toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  const sendMessage = () => {
    if (!draft.trim()) return;
    const newMsg = {
      id: "out-" + Date.now(),
      direction: "out",
      text: draft.trim(),
      time: nowTime(),
      status: "delivered",
    };
    setMessages((prev) => [...prev, newMsg]);
    setDraft("");

    // Simulate the "delivered → seen" transition after 1.2s
    setTimeout(() => {
      setMessages((prev) =>
        prev.map((m) => (m.id === newMsg.id ? { ...m, status: "seen" } : m)),
      );
    }, 1200);
  };

  /* Simulates a customer replying back via Instagram — used to demonstrate
     the webhook receive side of the two-way flow during the recording. */
  const simulateIncoming = () => {
    setSimulating(true);
    const replies = [
      "That sounds perfect! Can you share the total for 3 nights? 😊",
      "Great, and is breakfast included?",
      "Okay, can we do a video tour of Casa Azul?",
    ];
    const pick = replies[Math.floor(Math.random() * replies.length)];
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: "in-" + Date.now(),
          direction: "in",
          text: pick,
          time: nowTime(),
          status: "seen",
        },
      ]);
      setSimulating(false);
    }, 900);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header — Instagram style */}
        <header className="flex items-center justify-between border-b bg-white px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 text-white">
              <FaInstagram size={16} />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800">
                {lead?.name || lead?.customer_name || "Aarav Mehta"}
              </p>
              <p className="flex items-center gap-1 text-[11px] text-green-500">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                Instagram DM · Demo
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-xl text-gray-400 hover:text-gray-700"
            aria-label="Close"
          >
            <MdCancel />
          </button>
        </header>

        {/* Demo banner */}
        {/* <div className="flex items-center justify-between bg-amber-50 px-4 py-1.5 text-[10px] font-semibold text-amber-700">
          <span>🧪 DEMO — messages are simulated for Meta review</span>
          <button
            onClick={simulateIncoming}
            disabled={simulating}
            className="flex items-center gap-1 rounded bg-amber-200 px-2 py-0.5 text-amber-800 hover:bg-amber-300 disabled:opacity-50"
          >
            <MdRefresh size={11} className={simulating ? "animate-spin" : ""} />
            {simulating ? "Receiving…" : "Simulate incoming"}
          </button>
        </div> */}

        {/* Message list */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto bg-white px-4 py-3"
          style={{ backgroundImage: "linear-gradient(#fafafa, #ffffff)" }}
        >
          {messages.map((m) => (
            <Bubble key={m.id} msg={m} />
          ))}
        </div>

        {/* Composer */}
        <div className="flex items-center gap-2 border-t bg-white px-3 py-2.5">
          <button className="text-xl text-gray-400 hover:text-gray-600">
            <BsEmojiSmile />
          </button>
          <button className="text-xl text-gray-400 hover:text-gray-600">
            <BsImage />
          </button>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Message…"
            className="h-9 flex-1 rounded-full border border-gray-300 bg-gray-50 px-3 text-sm outline-none focus:border-[#3897f0] focus:bg-white"
          />
          <button
            onClick={sendMessage}
            disabled={!draft.trim()}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#3897f0] to-[#833ab4] text-white transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
          >
            <FaPaperPlane size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
